import { useEffect, useState } from "react"
import Loader from "../shared/loader"
import { ChecklistRow } from "./checklistRow"

export const HealthCheck = ({ isLoading, allChecklistStatuses, results, refresh, location }) => {
    const [showChecklistCategory, setShowChecklistCategory] = useState(location?.state?.initialCategory ?? "all")

    useEffect(() => {
        if (location?.state?.initialCategory) {
            setShowChecklistCategory(location.state.initialCategory)
        }
    }, [location?.state?.initialCategory])

    const filterPillColors = {
        all: "#748194",
        danger: "#e63757",
        warning: "#f5803e",
        success: "#00d27a"
    }

    const filterPillBaseStyle = {
        borderWidth: "3px", borderStyle: "solid", fontWeight: 700, fontSize: "14px", cursor: "pointer"
    }

    const filterPillStyle = category => {
        return showChecklistCategory === category
            ? { ...filterPillBaseStyle, borderColor: filterPillColors[category], backgroundColor: filterPillColors[category], color: "#fff" }
            : { ...filterPillBaseStyle, borderColor: filterPillColors[category], backgroundColor: "#fff", color: filterPillColors[category]  }
    }

    const filterPillIconStyle = category => (
        showChecklistCategory === category ? { color: "#fff" } : {color: filterPillColors[category]}
    )

    const {
        kadoeSetupResult,
        pcnPortalResult,
        paymentProviderResult,
        cashlessProvidersResult,
        cancellationAndVoidReasonsResult,
        referenceNumberRangesResult,
        clientsResult,
        sitesSetupResult,
        ipcOrBpaAccreditationResult,
        ipcBpaInSitesResult,
        pofaResult,
        letterTemplatesResult,
        contraventionsResult,
        siteAnprContraventionsResult
    } = results

    const checklistItems = [
        { key: "kadoe-connection", label: "KADOE Connection", description: "Confirms the organisation can look up registered keeper details from the DVLA via the Kadoe gateway.", result: kadoeSetupResult },
        { key: "reference-number-ranges", label: "Reference Number Ranges", description: "Every active contravention type has a PCN reference number range assigned to it.", result: referenceNumberRangesResult },
        { key: "ipc-bpa-accreditation", label: "IPC/BPA Accreditation", description: "Confirms IPC-accredited organisations have IAS export configured, or BPA-accredited organisations have a POPLA prefix set", result: ipcOrBpaAccreditationResult },
        { key: "ipc-bpa-statuses-for-sites", label: "IPC/BPA Site Statuses", description: "Confirms every active site has their IPC or BPA status enabled", result: ipcBpaInSitesResult },
        { key: "cashless-providers", label: "Cashless Providers", description: "Confirms every cashless provider referenced by a site also has its API key configured at organisation level.", result: cashlessProvidersResult },
        { key: "pcn-portal", label: "PCN Portal", description: "Confirms the public PCN portal has a host name configured, so motorists can view and pay their PCNs online.", result: pcnPortalResult },
        { key: "payment-provider", label: "Payment Provider", description: "Confirms a payment provider is selected and, if Stripe, that its keys are configured.", result: paymentProviderResult },
        { key: "cancellation-void-reasons", label: "Cancellation/Void Reasons", description: "Allows staff members to action breaches that need cancelling or voiding", result: cancellationAndVoidReasonsResult },
        { key: "clients-list", label: "Clients", description: "Allows staff members to add sites and start processing parking sessions", result: clientsResult },
        { key: "letter-templates", label: "Letter Templates", description: "Confirms every letter template is active, so appeal and cancellation correspondence goes out correctly", result: letterTemplatesResult },
        { key: "sites-setup", label: "Sites", description: "Confirms every site is active, so enforcement can actually take place there", result: sitesSetupResult },
        { key: "pofa-enabled", label: "PoFA", description: "Confirms every active England & Wales site has PoFA keeper-liability enabled", result: pofaResult },
        { key: "contraventions", label: "Contraventions", description: "Confirms every active contravention has its charge amounts and day windows configured", result: contraventionsResult },
        { key: "site-anpr-contraventions", label: "ANPR Contraventions", description: "Confirms every active site-contravention applied in ANPR settings is actually enabled for that site", result: siteAnprContraventionsResult }
    ]
        .filter(item => item.result !== null)
        .filter(item => showChecklistCategory === "all" || item.result.status === showChecklistCategory)

    return (
        <div className="container-fluid health-check-main-page">
            <div className="row">
                <div className="col-12">
                    <div className="card mb-3">
                        <div className="card-header px-4">
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                    <h5 className="mb-0 mr-2">Health Check</h5>
                                    <div
                                        className="badge badge-secondary d-flex align-items-center p-0"
                                        style={ filterPillStyle("all") }
                                        role="button"
                                        tabIndex={ 0 }
                                        onClick={ () => setShowChecklistCategory("all") }
                                        onKeyDown={ event => (event.key === "Enter" || event.key === " ") && setShowChecklistCategory("all") }
                                    >
                                        <div className="d-flex align-items-center justify-content-center"><p className="m-0 mr-2">All</p><span>{ allChecklistStatuses.length }</span></div>
                                        <span className="d-flex align-items-center btn-sm m-0 py-2 px-2 border-left"><i className="fas fa-filter m-0 p-0" style={ filterPillIconStyle("all") }></i></span>
                                    </div>
                                    <div
                                        className="badge badge-danger d-flex align-items-center p-0 ml-2"
                                        style={ filterPillStyle("danger") }
                                        role="button"
                                        tabIndex={ 0 }
                                        onClick={ () => setShowChecklistCategory("danger") }
                                        onKeyDown={ event => (event.key === "Enter" || event.key === " ") && setShowChecklistCategory("danger") }
                                    >
                                        <div className="d-flex align-items-center justify-content-center"><p className="m-0 mr-2">Critical</p><span>{ allChecklistStatuses.filter(item => item === "danger").length }</span></div>
                                        <span className="d-flex align-items-center btn-sm m-0 py-2 px-2 border-left"><i className="fas fa-filter m-0 p-0 " style={ filterPillIconStyle("danger") }></i></span>
                                    </div>

                                    <div
                                        className="badge badge-warning d-flex align-items-center p-0 ml-2"
                                        style={ filterPillStyle("warning") }
                                        role="button"
                                        tabIndex={ 0 }
                                        onClick={ () => setShowChecklistCategory("warning") }
                                        onKeyDown={ event => (event.key === "Enter" || event.key === " ") && setShowChecklistCategory("warning") }
                                    >
                                        <div className="d-flex align-items-center justify-content-center"><p className="m-0 mr-2">Warning</p><span>{ allChecklistStatuses.filter(item => item === "warning").length }</span></div>
                                        <span className="d-flex align-items-center btn-sm m-0 py-2 px-2 border-left"><i className="fas fa-filter m-0 p-0 " style={ filterPillIconStyle("warning") }></i></span>
                                    </div>

                                    <div
                                        className="badge badge-success d-flex align-items-center p-0 ml-2"
                                        style={ filterPillStyle("success") }
                                        role="button"
                                        tabIndex={ 0 }
                                        onClick={ () => setShowChecklistCategory("success") }
                                        onKeyDown={ event => (event.key === "Enter" || event.key === " ") && setShowChecklistCategory("success") }
                                    >
                                        <div className="d-flex align-items-center justify-content-center"><p className="m-0 mr-2">Complete</p><span>{ allChecklistStatuses.filter(item => item === "success").length }</span></div>
                                        <span className="d-flex align-items-center btn-sm m-0 py-2 px-2 border-left"><i className="fas fa-filter m-0 p-0" style={ filterPillIconStyle("success") }></i></span>
                                    </div>

                                </div>
                                <button onClick={ () => refresh() } type="button" className="btn btn-primary btn-sm">Refresh</button>
                            </div>
                        </div>

                        { isLoading && <Loader /> }
                        {
                            !isLoading &&
                            <div className="px-4">
                                { checklistItems.map(item => (
                                    <ChecklistRow
                                        key={ item.key }
                                        actions={ item.result.actions }
                                        description={ item.description }
                                        label={ item.label }
                                        status={ item.result.status }
                                        statusDetails={ item.result.statusDetails }
                                    />
                                )) }
                            </div>
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}
